import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { Observable, map, take } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Service that implements route guard functionality to protect routes based on authentication
 * and role-based access control.
 * 
 * This guard checks if the user is authenticated and has the required roles (if specified)
 * before allowing access to protected routes.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  /**
   * Determines if a route can be activated based on authentication status and role requirements.
   * 
   * @param route - The route being activated
   * @param state - The router state snapshot
   * @returns Observable<boolean> - True if the route can be activated, false otherwise
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.authService.isAuthenticated().pipe(
      take(1),
      map((authenticated) => {
        if (!authenticated) {
          this.router.navigate(['/auth/sign-in'], {
            queryParams: { returnUrl: state.url },
            replaceUrl: true
          });
          return false;
        }

        if (route.data['roles']?.length > 0) {
          const hasRequiredRole = route.data['roles'].some((role: string) =>
            this.authService.hasRole(role)
          );

          if (!hasRequiredRole) {
            this.router.navigate(['/error/access-denied']);
            return false;
          }
        }

        return true;
      })
    );
  }
}